import React from 'react';

import { Props } from '../props';
import { tabs } from '../setting';
import { URL } from '@Pages/url';
import NoImage from '@Images/noimage.webp';
import s from '../styles/Detail.module.css';
import { cleanCourseName } from '@Util/util';
// component
import Heading1 from '@Components/heading_1/view';
import Page from '@Components/pc/page/view';
import Breadcrumb from '@Components/breadcrumb/view';
import CourseTitle from '../components/course_title/view';
import CourseInfo from '../components/course_info/view';
import ImageSlider from '@Components/image_slider/view';
import Tab from '@Components/tab/view';
import ClubInfo from '../components/club_info';
import Access from '../components/access';
import Buying from '../components/buying';
import Selling from '../components/selling';
import News from '../components/news';
import Blog from '../components/blog';
import RecommendCourseList from '@Components/recommend_course_list/view';
import BuyingPriceTable from './components/buying_price_table/view';
import Playfee from './components/club_info_play_fee/view';
import SellingPriceTable from './components/selling_price_table/view';
import PriceTrendChart from './components/price_trend_chart/view';
import MemberVoiceCard from '@Components/member_voice_card/view';
import CourseInterviewRow from '@Components/course_interview_row/view';
import FooterInquiry from '@Components/footer_inquiry/view';

// currentPathはRailsのProps（業務データ）に含めず、packs/application.js・server-bundle.jsの
// register関数がrailsContext.pathnameから渡すpropとして別枠で受け取る（PR #2082 Point2対応）
type DetailProps = Props & { currentPath?: string };

const Detail = (props: DetailProps) => {
    const isProduction = process.env.NODE_ENV === 'production';
    const breadcrumbs = props.breadcrumbs;
    const [tab, setTab] = React.useState('club');
    const noImage: SliderImage[] = [{ url: NoImage }];
    const onlyInitialOfferingMemberships =
        props.memberships.length === props.memberships.filter((v) => v.isInitialOffering).length;
    const displayNewContent = props.news.length;

    const tabList = tabs.filter((t) => {
        if (t.key === 'selling' && (props.memberships.length === 0 || onlyInitialOfferingMemberships)) return false;
        if (t.key === 'buying' && props.memberships.length === 0) return false;

        if (t.key === 'news' && !displayNewContent) return false;

        return true;
    });

    // タブ一覧に出ないパネルはユーザーが開けないため、DOMにも出さない（Newsの既存判定と同じ考え方）
    const hasTab = (key: string) => tabList.some((t) => t.key === key);

    return (
        <Page
            contentHead={<Breadcrumb breadcrumbList={breadcrumbs} withoutBackground={true} />}
            courseId={props.id}
            currentPath={props.currentPath}
        >
            <div className={s.detail_main}>
                <div className={s.detail_main_inner}>
                    <div className={s.detail_image}>
                        {props.thumbnailImages.length ? (
                            <ImageSlider
                                altPrefix={props.courseName}
                                mainImages={props.mainImages}
                                thumbnailImages={props.thumbnailImages}
                            />
                        ) : (
                            <ImageSlider altPrefix={props.courseName} mainImages={noImage} thumbnailImages={noImage} />
                        )}
                    </div>
                    <div className={s.detail_info}>
                        <CourseTitle courseName={props.courseName} tags={props.tags} />
                        <CourseInfo
                            memberships={props.memberships}
                            courseId={props.id}
                            isJpy={props.isJpy}
                            isSp={false}
                            course={props.course}
                        />
                    </div>
                </div>
            </div>
            <Tab tabs={tabList} current={tab} onClick={setTab} />
            <div className={s.detail_contents}>
                <ClubInfo
                    show={tab === 'club'}
                    course={props.course}
                    courseName={props.courseName}
                    courseKanaName={props.courseKanaName}
                    holiday={props.course.holiday}
                    isSp={false}
                >
                    {isProduction || <Playfee courseName={cleanCourseName(props.courseName)} />}
                </ClubInfo>
                <Access
                    show={tab === 'access'}
                    course={props.course}
                    highwayInformation={props.highwayInformation}
                    lineInformation={props.lineInformation}
                    courseName={cleanCourseName(props.courseName)}
                />
                {hasTab('buying') && (
                    <Buying
                        show={tab === 'buying'}
                        course={props.course}
                        courseName={cleanCourseName(props.courseName)}
                        courseId={props.id}
                    >
                        <BuyingPriceTable memberships={props.memberships} isJpy={props.isJpy} />
                    </Buying>
                )}
                {hasTab('selling') && (
                    <Selling
                        show={tab === 'selling'}
                        courseId={props.id}
                        courseName={cleanCourseName(props.courseName)}
                    >
                        <SellingPriceTable memberships={props.memberships} isJpy={props.isJpy} />
                    </Selling>
                )}
                {hasTab('news') && (
                    <News show={tab === 'news'} news={props.news} courseName={cleanCourseName(props.courseName)} />
                )}
            </div>
            <PriceTrendChart
                priceTrend={props.priceTrend}
                courseName={cleanCourseName(props.courseName)}
                isJpy={props.isJpy}
            />
            {props.staffBlogs.length ? (
                <Blog
                    staffBlogs={props.staffBlogs}
                    courseName={cleanCourseName(props.courseName)}
                    courseId={props.id}
                />
            ) : (
                ''
            )}
            {props.courseInterviews && props.courseInterviews.length > 0 && (
                <div className={s.course_interview_contents}>
                    <Heading1 title={`ゴルフ場インタビュー`} />
                    <div className={s.course_interviews}>
                        {props.courseInterviews.map((courseInterview, i) => (
                            <CourseInterviewRow
                                id={courseInterview.id}
                                image={courseInterview.thumbnailImage}
                                title={courseInterview.title}
                                memberName={courseInterview.memberName}
                                work={`${courseInterview.subtext1} ${courseInterview.subtext2}`}
                                courses={courseInterview.courses}
                                key={i}
                            />
                        ))}
                    </div>
                    <div className={s.link}>
                        <a href={URL.COURSE_INTERVIEWS}>ゴルフ場インタビューをもっと見る</a>
                    </div>
                </div>
            )}
            {props.memberVoices.length > 0 && (
                <div className={s.member_voice_contents}>
                    <Heading1 title={`${props.courseName}のメンバーの声`} />
                    <div className={s.member_voices}>
                        {props.memberVoices.map((memberVoice, i) => (
                            <MemberVoiceCard
                                id={memberVoice.id}
                                image={memberVoice.thumbnailImage}
                                title={memberVoice.title}
                                memberName={memberVoice.memberName}
                                work={`${memberVoice.job1} ${memberVoice.job2}`}
                                courses={memberVoice.courses}
                                key={i}
                            />
                        ))}
                    </div>
                    <div className={s.link}>
                        <a href={URL.MEMBER_VOICES}>お客様の声をもっと見る</a>
                    </div>
                </div>
            )}

            <RecommendCourseList
                courses={props.recommendCourses.courses}
                prefecture={props.recommendCourses.prefecture}
            />

            <FooterInquiry courseId={props.id} />
        </Page>
    );
};

export default Detail;
